/** Frontend API client — never trusts server-side secrets; all prices validated server-side */

import type { AccountStandingSnapshot } from '../../shared/pairvelle'
import type { MessageStrikeInfo } from '../../shared/message-moderation'
import type { ProfileCompletionSnapshot } from '../../shared/onboarding'
import { apiUrl, mediaUrl } from './apiBase'
import { getNativeSessionToken, setNativeSessionToken } from './nativeSession'

export type AppNotification = {
  id: string
  type: string
  title: string
  body: string | null
  data: Record<string, unknown> | null
  readAt: string | null
  createdAt: string
}

const CLIENT_HEADER = { 'X-Opposites-Client': 'pairvelle-web' }

function persistSessionFromResponse(data: unknown) {
  if (!data || typeof data !== 'object') return
  const token = (data as { sessionToken?: string }).sessionToken
  if (typeof token === 'string' && token.length > 10) setNativeSessionToken(token)
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('X-Opposites-Client', CLIENT_HEADER['X-Opposites-Client'])
  const nativeToken = getNativeSessionToken()
  if (nativeToken) {
    headers.set('X-Session-Token', nativeToken)
  }
  if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  const res = await fetch(apiUrl(path), { ...init, headers, credentials: 'include' })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const payload = data as { error?: string; reason?: string }
    if (res.status === 401 || res.status === 500) setNativeSessionToken(null)
    const msg =
      payload.error === 'message_suspended' && (payload as { reason?: string }).reason
        ? String((payload as { reason?: string }).reason)
        : (payload.error ?? res.statusText)
    const err = new Error(msg) as Error & {
      status: number
      body: unknown
    }
    err.status = res.status
    err.body = data
    throw err
  }
  persistSessionFromResponse(data)
  return data as T
}

export type PhotoInfo = {
  id: string
  kind: 'public' | 'private'
  urlPath: string | null
  moderationStatus?: string
  sortOrder?: number
  updatedAt?: string | null
}

export const client = {
  health: () => api<{ ok: boolean; version: string; service: string }>('/api/health'),
  publicConfig: () =>
    api<{
      brand: string
      turnstileSiteKey: string | null
      googleOAuthEnabled: boolean
      unsolicitedMessageCredits: number
      messagingRules: unknown
      environment: string
    }>('/api/config/public'),
  me: () =>
    api<{
      user: Record<string, unknown> | null
      isAdmin?: boolean
      authMethod?: 'password' | 'google'
      onboarding?: ProfileCompletionSnapshot | null
    }>('/api/auth/me'),
  onboardingStatus: () => api<{ onboarding: ProfileCompletionSnapshot }>('/api/profiles/completion'),
  register: (body: {
    email: string
    password: string
    displayName: string
    ageConfirmed: boolean
    outreachPreference?: string
    dynamicRole?: string
    turnstileToken?: string
    affiliateCode?: string
  }) => api('/api/auth/register', { method: 'POST', body: JSON.stringify(body) }),
  login: (body: { email: string; password: string; turnstileToken?: string }) =>
    api<{ userId: string; isAdmin?: boolean }>('/api/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  requestPasswordReset: (email: string) =>
    api<{ ok: boolean; message: string; devResetToken?: string }>('/api/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  resetPassword: (token: string, password: string) =>
    api<{ ok: boolean; message?: string }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api<{ ok: boolean; message?: string }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  verifyEmail: (token: string) =>
    api<{ ok: boolean }>('/api/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) }),
  googleAuthUrl: (
    intent: 'signin' | 'signup',
    opts?: { ageConfirmed?: boolean; returnTo?: string; platform?: string; affiliateCode?: string },
  ) => {
    const q = new URLSearchParams({ intent })
    if (opts?.ageConfirmed) q.set('ageConfirmed', '1')
    q.set('returnTo', opts?.returnTo ?? '/onboarding')
    if (opts?.platform) q.set('platform', opts.platform)
    if (opts?.affiliateCode) q.set('ref', opts.affiliateCode)
    return apiUrl(`/api/auth/google?${q}`)
  },
  exchangeOAuthCode: (code: string) =>
    api<{ ok: boolean; dest: string; sessionToken?: string }>('/api/auth/oauth/exchange', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  logout: () =>
    api('/api/auth/logout', { method: 'POST', body: '{}' }).finally(() => setNativeSessionToken(null)),
  feed: () =>
    api<{
      profiles: import('../../shared/types').PublicProfile[]
      emptyRoom?: {
        active: boolean
        searchableMembers: number
        joinedThisWeek: number
        completingProfiles: number
        foundingSlotsRemaining: number
        foundingCap: number
        recentUnlocks: number
        threshold: number
      }
    }>('/api/discover/feed'),
  like: (targetUserId: string) =>
    api<{ matched: boolean; conversationId?: string }>('/api/discover/like', {
      method: 'POST',
      body: JSON.stringify({ targetUserId }),
    }),
  pass: (targetUserId: string) =>
    api('/api/discover/pass', { method: 'POST', body: JSON.stringify({ targetUserId }) }),
  rewindPass: () =>
    api<{ ok: boolean; restoredUserId: string }>('/api/discover/pass/rewind', {
      method: 'POST',
      body: '{}',
    }),
  unlike: (targetUserId: string) =>
    api('/api/discover/unlike', { method: 'POST', body: JSON.stringify({ targetUserId }) }),
  likes: () => api<{ profiles: Array<import('../../shared/types').PublicProfile & { mutual?: boolean; likedBack?: boolean }> }>('/api/discover/likes'),
  likedMe: () =>
    api<{ profiles: Array<import('../../shared/types').PublicProfile & { mutual?: boolean; likedBack?: boolean }> }>(
      '/api/discover/liked-me',
    ),
  profileViewers: () =>
    api<{ viewers: Array<import('../../shared/types').PublicProfile & { viewedAt: string }> }>('/api/discover/viewers'),
  recordProfileView: (targetUserId: string) =>
    api('/api/discover/view', { method: 'POST', body: JSON.stringify({ targetUserId }) }),
  notifications: () =>
    api<{ notifications: AppNotification[]; unread: number }>('/api/notifications'),
  readNotifications: (ids?: string[], all?: boolean) =>
    api('/api/notifications/read', {
      method: 'POST',
      body: JSON.stringify(all ? { all: true } : { ids }),
    }),
  unmatch: (peerId: string) => api('/api/matches/unmatch', { method: 'POST', body: JSON.stringify({ peerId }) }),
  blockedUsers: () => api<{ blocked: Array<{ id: string; display_name: string | null }> }>('/api/safety/blocked'),
  conversations: (inbox?: 'matches' | 'requests') =>
    api<{ conversations: import('../../shared/types').ConversationSummary[] }>(
      inbox ? `/api/messages?inbox=${inbox}` : '/api/messages',
    ),
  messages: (id: string) =>
    api<{
      messages: Array<{
        id: string
        sender_id: string
        body: string
        created_at: string
        hasAttachment?: boolean
        attachmentUrl?: string | null
        readByPeer?: boolean
        blocked?: boolean
        blockReason?: string | null
        blockCategory?: string | null
        deliveryStatus?: string
      }>
      moderation?: MessageStrikeInfo
      conversation?: {
        kind: string
        isPaidIntro: boolean
        peerLastReadAt: string | null
        stakeStatus: string | null
        canDeclineIntro: boolean
        readReceiptsEnabled: boolean
      }
    }>(`/api/messages/${id}`),
  quote: (peerId: string) =>
    api<{ quote: import('../../shared/types').MessageQuote }>(`/api/messages/quote?peerId=${peerId}`),
  sendMessage: (body: {
    peerId: string
    text: string
    idempotencyKey: string
    confirmPaid?: boolean
    file?: File
  }) => {
    type SendResult = {
      ok: boolean
      blocked?: boolean
      messageId: string
      conversationId: string
      chargedCredits?: number
      reason?: string
      category?: string
      message?: {
        id: string
        sender_id: string
        body: string
        created_at: string
        hasAttachment?: boolean
        attachmentUrl?: string | null
        blocked?: boolean
        blockReason?: string | null
        blockCategory?: string | null
      }
      moderation?: MessageStrikeInfo
      strikes?: { current: number; limit: number; suspendedUntil: string | null; justSuspended?: boolean }
    }
    if (body.file) {
      const form = new FormData()
      form.set('peerId', body.peerId)
      form.set('text', body.text)
      form.set('idempotencyKey', body.idempotencyKey)
      if (body.confirmPaid) form.set('confirmPaid', 'true')
      form.set('file', body.file)
      return api<SendResult>('/api/messages/send', { method: 'POST', body: form })
    }
    return api<SendResult>('/api/messages/send', { method: 'POST', body: JSON.stringify(body) })
  },
  declineIntro: (conversationId: string) =>
    api('/api/messages/decline-intro', { method: 'POST', body: JSON.stringify({ conversationId }) }),
  updateProfile: (body: Record<string, unknown>) =>
    api('/api/profiles/me', { method: 'PATCH', body: JSON.stringify(body) }),
  profileDetail: async (id: string) => {
    const res = await api<{
      profile: Record<string, unknown>
      tags: string[]
      socialLinks?: import('../../shared/social-links').SocialLink[]
      photos: PhotoInfo[]
    }>(`/api/profiles/${id}`)
    return {
      ...res,
      socialLinks: res.socialLinks ?? [],
      photos: res.photos.map((p) => ({ ...p, urlPath: mediaUrl(p.urlPath) })),
    }
  },
  uploadPhoto: (file: File, kind: 'public' | 'private') => {
    const form = new FormData()
    form.set('file', file)
    form.set('kind', kind)
    return api<{ photoId: string; moderation: string; message?: string }>('/api/profiles/photos', { method: 'POST', body: form })
  },
  replacePhoto: (photoId: string, file: File) => {
    const form = new FormData()
    form.set('file', file)
    return api<{ photoId: string; moderation: string; message?: string; onboarding?: ProfileCompletionSnapshot }>(
      `/api/profiles/photos/${photoId}`,
      { method: 'PUT', body: form },
    )
  },
  reorderPhotos: (photoIds: string[]) =>
    api<{ ok: boolean }>('/api/profiles/photos/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ photoIds }),
    }),
  deletePhoto: (photoId: string) =>
    api<{ ok: boolean; onboarding?: ProfileCompletionSnapshot }>(`/api/profiles/photos/${photoId}`, {
      method: 'DELETE',
    }),
  standing: () => api<{ standing: AccountStandingSnapshot }>('/api/standing'),
  submitAppeal: (body: string, noticeId?: string) =>
    api<{ ok: boolean; appealId: string; message: string }>('/api/standing/appeal', {
      method: 'POST',
      body: JSON.stringify({ body, noticeId }),
    }),
  report: (body: { targetType: string; targetId: string; reason: string; details?: string; reportedId?: string }) =>
    api('/api/reports', { method: 'POST', body: JSON.stringify(body) }),
  deleteAccount: (password: string) =>
    api('/api/auth/delete-account', { method: 'POST', body: JSON.stringify({ password }) }),
  wallet: () =>
    api<
      import('../../shared/types').WalletSnapshot & {
        subscription?: { code: string; name: string; status: string; periodEnd: string | null }
        adsEnabled?: boolean
        entitlements?: import('../../shared/types').Entitlements
        planCode?: string
      }
    >('/api/wallet'),
  walletTransactions: () =>
    api<{
      transactions: Array<{
        id: string
        type: string
        memo: string | null
        created_at: string
        direction: string
        amount: number
      }>
    }>('/api/wallet/transactions'),
  affiliate: () =>
    api<{
      code: string
      referrals: number
      creditsEarned: number
      referrerReward: number
      refereeReward: number
      sharePath: string
      shareUrl: string
    }>('/api/wallet/affiliate'),
  buyCredits: (credits: number, idempotencyKey: string) =>
    api<{ mode: string; checkoutUrl: string | null; message: string; demoCreditsGranted?: number }>(
      '/api/wallet/credits/purchase',
      {
        method: 'POST',
        body: JSON.stringify({ credits, idempotencyKey }),
      },
    ),
  subscribePlus: (idempotencyKey: string) =>
    api<{ mode: string; checkoutUrl: string | null; message: string; demoSubscriptionGranted?: boolean }>(
      '/api/wallet/subscription/checkout',
      {
        method: 'POST',
        body: JSON.stringify({ idempotencyKey }),
      },
    ),
  billingPortal: () =>
    api<{ mode: string; url: string | null; message: string }>('/api/wallet/portal', {
      method: 'POST',
      body: '{}',
    }),
  submitSelfie: (file: Blob) => {
    const form = new FormData()
    form.set('file', file, 'selfie.jpg')
    return api<{ caseId: string; status: string; mode: string; message: string }>('/api/profiles/verify/selfie', {
      method: 'POST',
      body: form,
    })
  },
  verificationProvider: () =>
    api<{
      provider: 'rekognition' | 'facepp' | 'sightengine' | 'demo'
      region: string
      liveness: boolean
      cognitoIdentityPoolId: string | null
      message: string
    }>('/api/profiles/verify/provider'),
  createLivenessSession: () =>
    api<{
      provider: string
      sessionId: string
      region: string
      credentials: {
        accessKeyId: string
        secretAccessKey: string
        sessionToken: string
        expiration: string
      }
      cognitoIdentityPoolId: string | null
    }>('/api/profiles/verify/liveness/session', { method: 'POST', body: '{}' }),
  completeLivenessSelfie: (sessionId: string) =>
    api<{ caseId: string; status: string; mode: string; message: string }>('/api/profiles/verify/selfie/complete', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    }),
  verificationStatus: () =>
    api<{
      verificationStatus: string
      latestCase: { id: string; status: string; created_at: string; review_notes?: string } | null
      provider?: string
    }>('/api/profiles/verify/status'),
  ads: (slot: string) =>
    api<{
      ad: {
        html: string
        demo: boolean
        headline?: string
        body?: string
        cta?: string
        ctaHref?: string
      } | null
      interval?: { min: number; max: number }
    }>(`/api/ads/${slot}`),
  adminOverview: () => api('/api/admin/overview'),
  adminModeration: () => api<{ cases: Record<string, unknown>[] }>('/api/admin/moderation'),
  adminModerationDecide: (id: string, body: { status: string; notes?: string }) =>
    api(`/api/admin/moderation/${id}`, { method: 'POST', body: JSON.stringify(body) }),
  adminReports: () => api<{ reports: Record<string, unknown>[] }>('/api/admin/reports'),
  adminResolveReport: (id: string, status: 'resolved' | 'dismissed') =>
    api(`/api/admin/reports/${id}/resolve`, { method: 'POST', body: JSON.stringify({ status }) }),
  adminAppeals: (status = 'pending') => api<{ appeals: Record<string, unknown>[] }>(`/api/admin/appeals?status=${status}`),
  adminAppealDecide: (id: string, body: { status: 'granted' | 'denied' | 'reviewing'; response?: string }) =>
    api(`/api/admin/appeals/${id}`, { method: 'POST', body: JSON.stringify(body) }),
  adminUsers: () => api<{ users: Record<string, unknown>[] }>('/api/admin/users'),
  adminSuspendUser: (id: string, body: { reason?: string; permanent?: boolean }) =>
    api(`/api/admin/users/${id}/suspend`, { method: 'POST', body: JSON.stringify(body) }),
  adminReinstateUser: (id: string) => api(`/api/admin/users/${id}/reinstate`, { method: 'POST', body: '{}' }),
  adminPromoteUser: (id: string) =>
    api<{ ok: boolean; message?: string }>(`/api/admin/users/${id}/promote`, { method: 'POST', body: '{}' }),
  adminDemoteUser: (id: string) =>
    api<{ ok: boolean; message?: string }>(`/api/admin/users/${id}/demote`, { method: 'POST', body: '{}' }),
  adminSupport: (status = 'open') => api<{ tickets: Record<string, unknown>[] }>(`/api/admin/support?status=${status}`),
  adminSupportUpdate: (id: string, body: { status?: string; reply?: string }) =>
    api(`/api/admin/support/${id}`, { method: 'POST', body: JSON.stringify(body) }),
  adminAudit: () => api<{ logs: Record<string, unknown>[] }>('/api/admin/audit'),
  adminMediaUrl: (photoId: string) => `/api/admin/media/${photoId}`,
  blockUser: (userId: string) => api('/api/safety/block', { method: 'POST', body: JSON.stringify({ userId }) }),
  unblockUser: (userId: string) => api(`/api/safety/block/${userId}`, { method: 'DELETE' }),
  legal: (doc: string) => api<{ doc: string; status: string; body: string }>(`/api/legal/${doc}`),
  supportChat: (messages: { role: 'user' | 'assistant'; content: string }[]) =>
    api<{ mode: string; reply: string }>('/api/support/chat', {
      method: 'POST',
      body: JSON.stringify({ messages }),
    }),
  submitTicket: (body: { kind: 'support' | 'feedback' | 'bug'; subject?: string; body: string }) =>
    api<{ id: string; status: string; message: string }>('/api/support/tickets', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  myTickets: () =>
    api<{
      tickets: {
        id: string
        kind: string
        subject: string | null
        body: string
        status: string
        admin_reply: string | null
        created_at: string
        updated_at: string
      }[]
    }>('/api/support/tickets'),
  photoRequestSettings: () =>
    api<{ settings: import('../../shared/photo-requests').PhotoRequestSettings; allowlist: Array<{ id: string; display_name: string }> }>(
      '/api/photo-requests/settings/me',
    ),
  updatePhotoRequestSettings: (body: Partial<import('../../shared/photo-requests').PhotoRequestSettings>) =>
    api<{ ok: boolean; settings: import('../../shared/photo-requests').PhotoRequestSettings }>('/api/photo-requests/settings/me', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  photoRequestComposerSettings: (userId: string) =>
    api<{
      enabled: boolean
      settings: {
        allowedCategories: import('../../shared/photo-requests').PhotoRequestCategory[]
        minAmountCredits: number
        faceRequired: boolean
        requestExpiryHours: number
        reuseRights: string
      } | null
    }>(`/api/photo-requests/settings/${userId}`),
  photoRequestsForConversation: (conversationId: string) =>
    api<{ requests: Record<string, unknown>[] }>(`/api/photo-requests/conversation/${conversationId}`),
  createPhotoRequest: (body: {
    creatorId: string
    category: import('../../shared/photo-requests').PhotoRequestCategory
    promptNotes?: string
    amountCredits: number
    idempotencyKey: string
  }) =>
    api<{ ok: boolean; requestId: string; conversationId?: string; status: string; expiresAt?: string }>(
      '/api/photo-requests',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  respondPhotoRequest: (id: string, body: { action: 'accept' | 'decline' | 'counter'; counterAmountCredits?: number }) =>
    api('/api/photo-requests/' + id + '/respond', { method: 'POST', body: JSON.stringify(body) }),
  acceptPhotoRequestCounter: (id: string) =>
    api('/api/photo-requests/' + id + '/accept-counter', { method: 'POST', body: '{}' }),
  deliverPhotoRequest: (id: string, file: File) => {
    const form = new FormData()
    form.set('file', file)
    return api('/api/photo-requests/' + id + '/deliver', { method: 'POST', body: form })
  },
  confirmPhotoRequest: (id: string) =>
    api('/api/photo-requests/' + id + '/confirm', { method: 'POST', body: '{}' }),
  disputePhotoRequest: (id: string, body: { reason: import('../../shared/photo-requests').PhotoRequestDisputeReason; details?: string }) =>
    api('/api/photo-requests/' + id + '/dispute', { method: 'POST', body: JSON.stringify(body) }),
}

export function newIdempotencyKey(prefix: string): string {
  return `${prefix}_${crypto.randomUUID()}`
}
